import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators';
import { OptionInterface } from '../../../../core/models/option.interface';
import { UserRole } from '../../../../core/models/user-role';
import { UserRoleApiService } from '../../../../core/services/api/user-role-api.service';
import { find } from '../../../../core/utils/collection.utils';
import { getCurrentMillis, getMoment } from '../../../../core/utils/date.utils';
import { buildOptions } from '../../../../core/utils/option.utils';
import { formChanged } from '../../../../core/validators/form-changed.validator';
import { AbstractEditionModalForm } from '../edition-modal/abstract-edition-modal-form';

export type UrgentOrClaimedType = 'urgent' | 'claimed';

const URGENT_PREFIX_KEY = 'dashboard.manufacturingOrderArticle.viewer.list.tabs.information.urgent.form.urgent';
const CLAIMED_PREFIX_KEY = 'dashboard.manufacturingOrderArticle.viewer.list.tabs.information.claimed.form.claim';

/**
 * Unified modal opened from the BY_PART planner long-press on the OF code cell.
 * Replaces the two separate `urgent-manufacturing-order-form` and
 * `claimed-manufacturing-order-form` modals in the planner — the article-viewer
 * keeps using the original forms unchanged.
 *
 * The user picks a type (default `urgent`) via radio buttons, types a comment
 * (with auto-prefix that switches with the type), and saves. The parent routes
 * the payload to either /urgent-manufacturing-orders or /claimed-manufacturing-orders
 * based on `getFormData().type`.
 */
@Component({
  selector: 'app-urgent-or-claimed-form',
  templateUrl: './urgent-or-claimed-form.component.html',
  styleUrls: ['./urgent-or-claimed-form.component.css'],
  providers: [
    {
      provide: AbstractEditionModalForm,
      useExisting: forwardRef(() => UrgentOrClaimedFormComponent),
    },
  ],
})
export class UrgentOrClaimedFormComponent extends AbstractEditionModalForm<any> implements OnInit, OnChanges {
  selectedReciver: {
    hash: string;
    label: string;
    isRole: boolean;
    isUser: boolean;
  }[] = [];
  form: AbstractControl;
  receiverOptions: OptionInterface[];
  @Input() usersRoles: UserRole[];
  /**
   * When true the recipients selector is hidden and the names are resolved
   * automatically from `recipientNames` (the BY_PART planner flow). Off in
   * any future legacy reuse.
   */
  @Input() useRecipientsMatrix = false;
  @Input() recipientNames: string[] = [];
  @Output() searchReceiver = new EventEmitter<string>();

  private urgentPrefix = '';
  private claimedPrefix = '';

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private userRoleApiService: UserRoleApiService
  ) {
    super();
  }

  ngOnInit() {
    this.urgentPrefix = this.translate.instant(URGENT_PREFIX_KEY);
    this.claimedPrefix = this.translate.instant(CLAIMED_PREFIX_KEY);
    this.initForm();
    this.wireUpPrefixToType();
    if (this.useRecipientsMatrix) {
      this.resolveMatrixRecipients();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('usersRoles')) {
      this.buildUsersRolesOptions();
    }
    if ((changes.hasOwnProperty('recipientNames') || changes.hasOwnProperty('useRecipientsMatrix'))
        && this.useRecipientsMatrix && this.form) {
      this.resolveMatrixRecipients();
    }
  }

  buildUsersRolesOptions() {
    if (this.selectedReciver && this.usersRoles) {
      const userRolesFiltered = this.usersRoles.filter(
        (userRole) =>
          !this.selectedReciver.some((item) => item.hash === userRole.hash)
      );
      this.receiverOptions = buildOptions(userRolesFiltered, 'hash', 'name');
    }
  }

  onSelect(event) {
    if (!this.selectedReciver.some((item) => item.hash === event.value)) {
      const userRole: UserRole = find(
        (ur: UserRole) => ur.hash === event.value,
        this.usersRoles
      );
      this.selectedReciver.push({
        hash: event.value,
        label: event.label,
        isRole: userRole ? userRole.isRole : null,
        isUser: userRole ? userRole.isUser : null,
      });
      this.buildUsersRolesOptions();
    }
    this.syncHasReciverFlag();
    this.form.get('receiverHash').setValue('');
  }

  removeItem(item: any) {
    const index = this.selectedReciver.indexOf(item);
    if (index !== -1) {
      this.selectedReciver.splice(index, 1);
      this.buildUsersRolesOptions();
    }
    this.syncHasReciverFlag();
  }

  /**
   * Resolves each configured name via /users-roles/search (one call per name).
   * The backend search is `firstName LIKE %q% OR lastName LIKE %q%` so we send
   * only the first token and filter exact full-name match in the client.
   * Unmatched names are silently skipped.
   */
  private resolveMatrixRecipients() {
    this.selectedReciver = [];
    if (!this.recipientNames || this.recipientNames.length === 0) {
      this.syncHasReciverFlag();
      return;
    }
    const searches = this.recipientNames.map(name => {
      const firstToken = (name || '').trim().split(/\s+/)[0];
      if (!firstToken) {
        return of([] as UserRole[]);
      }
      return this.userRoleApiService.searchUsersAndRoles(firstToken).pipe(catchError(() => of([])));
    });
    forkJoin(searches).subscribe(results => {
      this.recipientNames.forEach((name, idx) => {
        const matches = (results[idx] as UserRole[]) || [];
        const exact = matches.find(ur => ur.isUser && this.namesMatch(ur.name, name));
        if (exact && !this.selectedReciver.some(item => item.hash === exact.hash)) {
          this.selectedReciver.push({
            hash: exact.hash,
            label: exact.name,
            isRole: false,
            isUser: true,
          });
        }
      });
      this.syncHasReciverFlag();
    });
  }

  private namesMatch(a: string, b: string): boolean {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  private syncHasReciverFlag() {
    if (!this.form) {
      return;
    }
    if (this.selectedReciver.length > 0) {
      this.form.get('hasReciver').setValue(true);
    } else {
      this.form.get('hasReciver').setValue(null);
    }
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  initForm() {
    this.form = this.fb.group({
      type: ['urgent' as UrgentOrClaimedType, [Validators.required]],
      hasReciver: [null, [Validators.required]],
      receiverHash: [''],
      comment: ['', [Validators.required, Validators.maxLength(255)]],
      date: [getMoment(getCurrentMillis()), Validators.required],
    });
  }

  /**
   * Keeps the comment textarea prefixed with the current type's label.
   * Switching the radio rewrites the prefix; typing strips the prefix only
   * if the user manually removes it (then we re-add silently).
   */
  private wireUpPrefixToType() {
    // Seed with the default type's prefix.
    this.form.get('comment').setValue(this.urgentPrefix, { emitEvent: false });

    this.form.get('type').valueChanges.subscribe((type: UrgentOrClaimedType) => {
      const newPrefix = type === 'claimed' ? this.claimedPrefix : this.urgentPrefix;
      const oldPrefix = type === 'claimed' ? this.urgentPrefix : this.claimedPrefix;
      const current = this.form.get('comment').value || '';
      // Replace the previous prefix if it was there, otherwise just prepend.
      const trailing = current.startsWith(oldPrefix) ? current.substring(oldPrefix.length) : current;
      this.form.get('comment').setValue(newPrefix + trailing, { emitEvent: false });
    });

    this.form.get('comment').valueChanges.subscribe((value: string) => {
      const expected = this.form.get('type').value === 'claimed' ? this.claimedPrefix : this.urgentPrefix;
      if (!value.startsWith(expected)) {
        this.form.get('comment').setValue(expected, { emitEvent: false });
      }
    });
  }

  updateForm(model?: any, _params?: any) {
    const value = {
      type: 'urgent' as UrgentOrClaimedType,
      receiverHash: model ? model.receiverHash : '',
      comment: this.urgentPrefix,
      date: model ? model.date : getMoment(getCurrentMillis()),
    };
    this.resetForm(value);
  }

  resetForm(value) {
    this.form.setValidators(formChanged(value));
    this.form.reset(value);
    // form.reset wipes hasReciver back to null even if matrix already resolved
    // earlier in ngOnInit; restore it here so save isn't stuck disabled when
    // the user opens the modal after the resolution finishes.
    this.syncHasReciverFlag();
  }

  getFormData() {
    const formValue = this.form.value;
    return {
      type: formValue.type as UrgentOrClaimedType,
      receivers: this.selectedReciver.map((reciver) => ({
        hash: reciver.hash,
        isRole: reciver.isRole,
        isUser: reciver.isUser,
      })),
      comment: formValue.comment,
      date: getMoment(formValue.date).valueOf(),
    };
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/shared/components/modals/urgent-or-claimed-form/urgent-or-claimed-form.component.ts